import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageUploadComponent } from '@app/shared/image-upload/image-upload.component';
import { AuthenticationService, DashboardService, LocationService, UserService } from '@app/_services';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-onboarding',
  templateUrl: 'onboarding.component.html',
})

export class OnboardingComponent implements OnInit {

  @ViewChild('imageUpload') imageUpload: ImageUploadComponent;
  profileUserImage: any;
  user: any;
  profileFormStep1: FormGroup;
  profileFormStep2: FormGroup;
  getUpdatesForm: FormGroup;
  shareProfileForm: FormGroup;
  countries: any[] = [];
  cities: any[] = [];
  states: any[] = [];
  sectors: any[] = [];
  submitted = false;
  loading: boolean = false;
  currentIndex: number;

  searchConsent: any = true;

  selectedSectors = [];
  steps = [
    { stepIndex: 0, isComplete: false, value: "" },
    { stepIndex: 1, isComplete: false, value: "" },
    { stepIndex: 2, isComplete: false, value: "" },
    { stepIndex: 3, isComplete: false, value: "" },
    { stepIndex: 4, isComplete: false, value: "" },
    { stepIndex: 5, isComplete: false, value: "" },
    { stepIndex: 6, isComplete: false, value: "" },
    { stepIndex: 7, isComplete: false, value: "" },
    { stepIndex: 8, isComplete: false, value: "" },
    { stepIndex: 9, isComplete: false, value: "" },
  ];

  validationMessages = {
    'firstName': [
      { type: 'required', message: 'Please fill your first name' },
    ],
    'lastName': [
      { type: 'required', message: 'Please fill your last name' }
    ],
    'aboutYou': [
        { type: 'required', message: 'Please tell us about you' }
    ],
    'company': [
        { type: 'required', message: 'Please enter your company name' }
    ],
    'companyRol': [
        { type: 'required', message: 'Please tell us about your company role' }
    ],
    'companyWebsite': [
        { type: 'required', message: 'Please enter your company website' },
        { type: 'pattern', message: 'Please enter a valid URL' }
    ],
    'aboutYourCompany': [
        { type: 'required', message: 'Please tell us about your company' }
    ],
    'country': [
        { type: 'required', message: 'Please select your country' },
    ],
    'city': [
        { type: 'required', message: 'Please select your city' },
    ],
    'state': [
        { type: 'stateConditionallyRequiredValidator', message: 'Please select your state' },
    ],
    'sectors': [
        { type: 'required', message: 'Please select one or more sectors' },
    ]
  };

  constructor(private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private locationService: LocationService,
    private authenticationService: AuthenticationService,
    private dashboardService: DashboardService,
    private formBuilder: FormBuilder) {

    this.getUpdatesForm = this.formBuilder.group({
      getUpdates: [false]
    });

    this.shareProfileForm = this.formBuilder.group({
      shareProfile: [false]
    });

    this.profileFormStep1 = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', Validators.required],
      aboutYou: [''],
    });

    this.profileFormStep2 = this.formBuilder.group({
      company: ['', Validators.required],
      companyRol: [''],
      country: [[], Validators.required],
      state: [],
      city: [[], Validators.required],
      sectors: [[], Validators.required],
      companyWebsite: ['', Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')],
      aboutYourCompany: [''],
    }, {
      validators: [this.stateConditionallyRequiredValidator]
    });
  }

  stateConditionallyRequiredValidator : ValidatorFn = (formGroup : FormGroup) => {
    return this.states?.length > 0 && !this.states.map(x => x.id).includes(formGroup.controls['state'].value)
            ? { stateConditionallyRequired: true }
            : null;
  }

  ngOnInit(): void {
    this.currentIndex = this.steps[0].stepIndex;
    this.user = this.authenticationService.currentUserValue;

    this.userService.getBy(this.user.id).subscribe(userProfile => {
      this.profileFormStep1.setValue({
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        emailAddress: userProfile.emailAddress,
        aboutYou: userProfile.aboutYou
      });

      this.profileUserImage = userProfile.userImageData;

      this.profileFormStep2.setValue({
        company: userProfile.company,
        companyRol: userProfile.companyRol,
        companyWebsite: userProfile.companyWebsite,
        aboutYourCompany: userProfile.aboutYourCompany,
        country: userProfile.location ? [{ id: userProfile.location?.countryId, itemName: userProfile.location?.countryName }] : [],
        city: userProfile.location ? [{ id: userProfile.location?.cityId, itemName: userProfile.location?.cityName }] : [],
        state: userProfile.location ? [{ id: userProfile.location?.stateId, itemName: userProfile.location?.stateName }] : [],
        sectors: userProfile.sectors.map(s => { return { id: s.id, itemName: s.name }; })
      });

      let countryId = userProfile.location?.countryId;

      if (!countryId) {
        return;
      }
      
      if (userProfile.location.stateId != null) {
        this.locationService.getStates(countryId).pipe(first()).subscribe(data => {
            this.states = data.map(i => {
                return {
                    id: i.id,
                    itemName: i.name
                };
            });
            this.locationService.getCitiesBySelectedStateId(userProfile.location.stateId).pipe(first()).subscribe(cityData => {
                this.cities = cityData.map(i => {
                    return {
                        id: i.id,
                        itemName: i.name
                    };
                });
            });
        });
      } else {
          this.locationService.getCitiesBySelectedCountryId(countryId).pipe(first()).subscribe(cityData => {
              this.cities = cityData.map(i => {
                  return {
                      id: i.id,
                      itemName: i.name
                  };
              });
          });
      }
    });
  }

  moveToNextStep() {
    this.steps[this.currentIndex].isComplete = true;
    this.currentIndex = this.steps[this.currentIndex + 1].stepIndex;

    if (this.currentIndex == 1) {
      this.imageUpload.imageSrc = this.profileUserImage;
    }

    if (this.currentIndex == 2) {
      this.getCountries();
      this.getSectors();
    }
  }

  GoDashboard() {
    this.loading = true;
    this.userService.finishOnboarding(this.user.id).pipe(first()).subscribe(() => {
      this.loading = false;

      this.authenticationService.finishOnboarding();
      this.router.navigate(['/dashboard']);
    });
  }

  onSubmitProfileFormStep1() {
    if (this.profileFormStep1.invalid) {
      return;
    }

    this.profileUserImage = this.imageUpload.imageSrc;
    this.moveToNextStep();
  }

  onSubmitProfileFormStep2() {
    Object.keys(this.profileFormStep2.controls).forEach(field => {
      const control = this.profileFormStep2.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      }
    });

    if (this.profileFormStep2.invalid) {
      return;
    }

    this.loading = true;
    let step1Values = this.profileFormStep1.getRawValue();
    let step2Values = this.profileFormStep2.getRawValue();
    delete step2Values.state;
    step2Values.cityId = step2Values.city;
    let data = {
      ...step1Values,
      ...step2Values,
      userImageData: this.profileUserImage,
      userId: this.authenticationService.currentUserValue.id
    };

    this.userService.update(data).pipe(first()).subscribe(() => {
      this.loading = false;
      this.moveToNextStep();
    });
  }

  onSubmitShareProfileForm() {
    this.loading = true;

    this.userService.setUserSearchConsent(this.user.id, this.searchConsent == 'yes').pipe(first()).subscribe(() => {
      this.loading = false;
      this.moveToNextStep();
    });
  }

  onSubmitGetUpdatesForm() {
    this.loading = true;
    this.userService.setMailUpdates(this.user.id, true).pipe(first()).subscribe(() => {
      this.loading = false;
      this.moveToNextStep();
    });
  }

  getCountries() {
    this.locationService.getAllCountries().pipe(first()).subscribe(data => {
      this.countries = data;
    });
  }

  
  countryValueChanged(value: any) {
    this.cities = [];
    this.states = [];

    this.profileFormStep2.controls['state'].setValue(null);
    this.profileFormStep2.controls['city'].setValue(null);

    if (!value || !value.id) {
        return;
    }
    let countryId = value.id;
    this.locationService.getStates(countryId).pipe(first()).subscribe(data => {
        if (data && data.length > 0) {
            this.states = data;
        } else {
            this.locationService.getCitiesBySelectedCountryId(countryId).pipe(first()).subscribe(data => {
                this.cities = data;
            }); 
        }
    });
}

stateValueChanged(value: any) {
    this.cities = [];
    this.profileFormStep2.controls['city'].setValue(null);

    if (!value || !value.id) {
        return;
    }

    let stateId = value.id;
    this.locationService.getCitiesBySelectedStateId(stateId).pipe(first()).subscribe(data => {
        this.cities = data;
    });
  }

  getSectors() {
    this.dashboardService.getSectors().pipe(first()).subscribe(data => {
      this.sectors = data;
    });
  }

  onItemSelect(sector: any) {
    this.selectedSectors.push(sector);
  }

  hasError(validation: any, controlName: string) {
    let control = this.profileFormStep1.get(controlName);
    if (!control) {
      control = this.profileFormStep2.get(controlName);
    }

    // special case: state
    if (controlName == 'state')
      return this.profileFormStep2.errors?.stateConditionallyRequired === true;

    return control.hasError(validation.type)
             && (control.dirty
             || control.touched);
  }
}
